/**
 * Spec 216 PR4 — StatusBadge primitive.
 *
 * Wraps the work status pill so the same chip styling lives in one
 * place. Uses the soft-tint tokens (`--c-{status}-chip-bg` +
 * `--c-{status}-text`) which are derived from the canonical
 * `--c-status-*` hues — same value chain as mobile (see DESIGN_SYSTEM
 * §6 / mobile/src/theme.ts pendingChipBg etc.). Keeps text contrast
 * AAA on the tinted bg in both light and dark.
 *
 * Use `<StatusBadge status={work.status} />` in lists, detail headers
 * and stats. The component renders a `<span role="status">` so screen
 * readers announce changes when the underlying value flips (web only —
 * mobile equivalent is the chip in WorkListScreen).
 */
import './StatusBadge.css';

export type WorkStatus = 'pending' | 'in_progress' | 'completed';

const LABEL: Record<WorkStatus, string> = {
  pending: 'Pendiente',
  in_progress: 'En curso',
  completed: 'Completado',
};

interface StatusBadgeProps {
  status: WorkStatus;
  /** Override the visible label (e.g. for shorter "Hecho" in compact lists). */
  label?: string;
  /** Test hook propagated as `data-testid`. */
  testId?: string;
}

export function StatusBadge({ status, label, testId }: StatusBadgeProps) {
  return (
    <span
      className={`status-badge status-badge--${status}`}
      role="status"
      data-status={status}
      data-testid={testId ?? 'status-badge'}
    >
      {label ?? LABEL[status]}
    </span>
  );
}
